import { useState } from 'react';
import { X, LogIn, Shield, AlertCircle } from 'lucide-react';
import { authAPI } from '../services/api';

interface MemberLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  language: 'ja' | 'en';
}

export default function MemberLoginModal({ isOpen, onClose, onSuccess, language }: MemberLoginModalProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const content = {
    ja: {
      title: 'メンバーログイン',
      subtitle: '登録コードを入力してください',
      placeholder: 'SAS-XXXX-XXXX',
      button: 'ログイン',
      buttonLoading: 'ログイン中...',
      cancel: 'キャンセル',
      errorInvalid: '無効な登録コードです',
      errorGeneral: 'ログインに失敗しました'
    },
    en: {
      title: 'Member Login',
      subtitle: 'Enter your registration code',
      placeholder: 'SAS-XXXX-XXXX',
      button: 'Login',
      buttonLoading: 'Logging in...',
      cancel: 'Cancel',
      errorInvalid: 'Invalid registration code',
      errorGeneral: 'Login failed'
    }
  };

  const t = content[language];

  function handleCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    let value = e.target.value.toUpperCase();

    // Remove all non-alphanumeric characters except hyphens
    value = value.replace(/[^A-Z0-9-]/g, '');

    // Remove existing hyphens to rebuild format
    value = value.replace(/-/g, '');

    // SAS code format
    if (!value.startsWith('SAS')) {
      if (value.length === 0) {
        value = 'SAS-';
      } else if (value.startsWith('SA')) {
        value = 'SAS' + value.substring(2);
      } else if (value.startsWith('S')) {
        value = 'SAS' + value.substring(1);
      } else {
        value = 'SAS' + value;
      }
    }

    // Remove 'SAS' prefix for formatting
    const afterSAS = value.substring(3);

    // Format as SAS-XXXX-XXXX
    let formatted = 'SAS';
    if (afterSAS.length > 0) {
      formatted += '-' + afterSAS.substring(0, 4);
    }
    if (afterSAS.length > 4) {
      formatted += '-' + afterSAS.substring(4, 8);
    }

    setCode(formatted);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authAPI.memberLogin(code);
      onSuccess();
    } catch (err: any) {
      setError(err.message || t.errorGeneral);
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{t.title}</h2>
              <p className="text-sm text-gray-600">{t.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="code" className="block text-sm font-semibold text-gray-700 mb-2">
              {language === 'ja' ? '登録コード' : 'Registration Code'}
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder={t.placeholder}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none font-mono text-lg"
              required
              disabled={loading}
              maxLength={15}
              autoFocus
            />
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 border-2 border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 text-gray-700 rounded-lg font-semibold transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={loading || code.length < 13}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors shadow-lg"
            >
              <LogIn className="w-5 h-5" />
              <span>{loading ? t.buttonLoading : t.button}</span>
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
